/**
 * Fix 9 (overnight 2026-05-05 Late) — null-safe string helpers.
 *
 * Pre-Fix-9 the dashboard crashed on a real prod assessment whose
 * `analytics/dashboard` response carried a `bodyRegions[].region`
 * that was `null` for a complaint with no body region tagged. The
 * direct call `r.region.toLowerCase()` at
 * `DashboardPage.tsx:380` threw, the React error boundary caught it,
 * and the clinician saw the global "Something went wrong" page
 * instead of the dashboard.
 *
 * The bug class is broader than that one site: any time the frontend
 * calls a string method on a value the TS types claim is `string`
 * but the backend can return `null` / `undefined` (or a non-string
 * altogether — e.g. a numeric MRN deserialised from JSON), the page
 * crashes. The user's brief calls this the ".includes() bug" but in
 * practice it's any of `.toLowerCase()`, `.split()`, `.match()`,
 * `.startsWith()`, `.endsWith()`, `.includes()`, `.trim()` etc.
 *
 * This module provides null-safe wrappers for the common operations
 * (lowercase, includes, split). Replaces the inline crashy calls at:
 *
 *   - frontend/src/pages/dashboard/DashboardPage.tsx
 *       formatMonth() — split on potentially-null `month` key
 *       region.toLowerCase() — body-region label lookup
 *   - frontend/src/pages/patients/PatientDetailPage.tsx
 *       patient.lastName.toLowerCase() — delete-confirm comparison
 *
 * Per the operational rule promoted after Fix 7 (extract to shared
 * helper at 3+ surfaces), these now live in a single module that
 * both `pages/` and `components/` consume. Future sweep work should
 * migrate any other unsafe call site to these helpers.
 *
 * Backlog: lift to `shared/src/safeString.ts` if a backend caller
 * needs the same semantics. Today the backend has its own
 * null-guards via Drizzle's typed nulls + safer DB query patterns,
 * so frontend-only is the right scope.
 */

/**
 * Coerce any value to a string, then `.toLowerCase()` it.
 *
 *   safeLower(null)        → ''
 *   safeLower(undefined)   → ''
 *   safeLower('Foo')       → 'foo'
 *   safeLower(42)          → '42'   // numeric MRN coerces cleanly
 *   safeLower(false)       → 'false'
 *   safeLower({})          → ''     // objects → empty (would format as "[object Object]" otherwise — useless for filtering)
 *
 * Object handling: a non-primitive returns `''` rather than the
 * `[object Object]` default. The point of these helpers is to make
 * filter / comparison code resilient; an object value in a string
 * slot is always a data-shape error and should fail to match,
 * never accidentally match the literal "[object" prefix.
 */
export function safeLower(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value).toLowerCase();
  }
  return '';
}

/**
 * Title-case a person's name for display, WITHOUT mangling names that are
 * already correctly cased. Only upper-cases the first letter that follows the
 * start of the string or a separator (space, hyphen, apostrophe); every other
 * character is left exactly as typed. So:
 *
 *   titleCaseName('hassan')      → 'Hassan'
 *   titleCaseName('McDonald')    → 'McDonald'   (internal capital preserved)
 *   titleCaseName("O'Brien")     → "O'Brien"
 *   titleCaseName('Jean-Luc')    → 'Jean-Luc'
 *   titleCaseName('de la cruz')  → 'De La Cruz'
 *   titleCaseName(null/123/{})   → ''
 *
 * It never lower-cases, so it is idempotent and safe on already-correct input.
 * Uses \p{L} (unicode) with the /u flag so EL/RU/HE names are handled.
 */
export function titleCaseName(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/(^|[\s\-'’])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/**
 * Null-safe `haystack.toLowerCase().includes(needle.toLowerCase())`.
 *
 *   safeIncludes(null, 'foo')  → false
 *   safeIncludes('Foo', null)  → false  (null/empty needle never matches; matches user intent for search filters)
 *   safeIncludes('Foo', '')    → false  (same — empty search returns no filter rather than match-all to keep filter UX predictable)
 *   safeIncludes('Foo', 'oo')  → true
 *   safeIncludes('FOO', 'foo') → true   (case-insensitive)
 *
 * The empty-needle behaviour is opinionated: callers that want "no
 * filter when the search box is empty" should branch BEFORE calling
 * this helper (e.g. `if (!search.trim()) return all`). This helper
 * encodes "match found" semantics, not "no-op when empty".
 */
export function safeIncludes(haystack: unknown, needle: unknown): boolean {
  const h = safeLower(haystack);
  const n = safeLower(needle);
  if (!h || !n) return false;
  return h.includes(n);
}

/**
 * Null-safe `String.prototype.split`. Returns `[]` rather than
 * throwing on `null` / `undefined`. Non-string values are coerced
 * (numbers / booleans / bigints) or yield `[]` (objects).
 *
 *   safeSplit(null, '-')       → []
 *   safeSplit('2026-04', '-')  → ['2026', '04']
 *   safeSplit(2026, '-')       → ['2026']
 *
 * Handles the dashboard's `formatMonth(d.month)` case where the
 * analytics payload for a clinic with no monthly trend rows can
 * come back with one entry whose `month` field is null.
 */
export function safeSplit(value: unknown, separator: string | RegExp): string[] {
  if (typeof value === 'string') return value.split(separator);
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value).split(separator);
  }
  return [];
}
